import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Motion";
import { api, formatApiError } from "@/lib/api";
import { img } from "@/lib/content";

const HIRING_TYPES = ["Direct Hire", "Executive Search", "Recruitment Process Outsourcing", "Talent Advisory", "Multiple Roles / Volume Hiring"];
const LOCATIONS = ["Dubai, UAE", "Abu Dhabi, UAE", "Riyadh, KSA", "Jeddah, KSA", "Doha, Qatar", "Kuwait City, Kuwait", "Manama, Bahrain", "Muscat, Oman", "Remote / Regional", "Other"];
const TIMELINES = ["Immediately", "Within 30 days", "1–3 months", "3–6 months", "Planning ahead"];

const HireTalent = () => {
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "", role_title: "",
    location: "", hiring_type: "", headcount: "1", timeline: "", salary_range: "", details: "", website: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/hire-requests", { ...form, source_page: "hire-talent" });
      setReference(r.data.reference);
      toast.success("Hiring requirement received.");
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const selectCls = "nx-input cursor-pointer appearance-none [&>option]:bg-obsidian";

  return (
    <>
      <Seo
        title="Hire Talent — NEXREQ UAE & GCC"
        siteName="NEXREQ"
        description="Submit your hiring requirement to NEXREQ. Our UAE & GCC team will review your request and respond within one business day."
      />
      <PageHero
        eyebrow="For Employers"
        lines={["Have a Role to Fill?"]}
        description="Tell us what you are hiring for. Our team will review your requirement and respond within one business day."
        image={img("photo-1556761175-b413da4baf72", 1920)}
      />

      <section className="bg-obsidian pb-28" data-testid="hire-section">
        <div className="nx-container max-w-3xl">
          {reference ? (
            <Reveal>
              <div className="border border-white/10 bg-surface p-10 lg:p-14" data-testid="hire-success">
                <span className="flex h-12 w-12 items-center justify-center bg-emerald-600/15 text-emerald-400">
                  <Check size={22} />
                </span>
                <h2 className="mt-7 font-heading text-3xl font-light text-white">Requirement Received.</h2>
                <p className="mt-5 leading-relaxed text-white/55">
                  Thank you for contacting NEXREQ. Your hiring requirement has been received. A
                  member of our UAE &amp; GCC team will review your request and contact you shortly.
                </p>
                <p className="mt-7 inline-block border border-white/10 bg-obsidian px-5 py-3 font-mono text-xs tracking-[0.15em] text-emerald-400">
                  REF: {reference}
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to="/employers" className="nx-btn-primary" data-testid="hire-success-employers-btn">
                    Explore Employer Solutions <ArrowRight size={15} />
                  </Link>
                  <Link to="/" className="nx-btn-outline" data-testid="hire-success-home-btn">
                    Return Home
                  </Link>
                </div>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="border border-white/10 bg-surface p-8 lg:p-12" data-testid="hire-form">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-500">Hiring Requirement</p>
                <h2 className="mt-4 font-heading text-2xl font-light text-white">The Brief</h2>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="nx-label" htmlFor="hr-company">Company *</label>
                    <input id="hr-company" className="nx-input" required value={form.company} onChange={set("company")} data-testid="hire-company-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-contact">Contact Name *</label>
                    <input id="hr-contact" className="nx-input" required value={form.contact_name} onChange={set("contact_name")} data-testid="hire-contact-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-email">Work Email *</label>
                    <input id="hr-email" type="email" className="nx-input" required value={form.email} onChange={set("email")} data-testid="hire-email-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-phone">Phone</label>
                    <input id="hr-phone" className="nx-input" value={form.phone} onChange={set("phone")} data-testid="hire-phone-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-role">Role Title *</label>
                    <input id="hr-role" className="nx-input" required placeholder="e.g. Head of Engineering" value={form.role_title} onChange={set("role_title")} data-testid="hire-role-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-location">Role Location *</label>
                    <select id="hr-location" className={selectCls} required value={form.location} onChange={set("location")} data-testid="hire-location-select">
                      <option value="">Select a location</option>
                      {LOCATIONS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-type">Hiring Type *</label>
                    <select id="hr-type" className={selectCls} required value={form.hiring_type} onChange={set("hiring_type")} data-testid="hire-type-select">
                      <option value="">Select a solution</option>
                      {HIRING_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-headcount">Headcount</label>
                    <input id="hr-headcount" className="nx-input" value={form.headcount} onChange={set("headcount")} data-testid="hire-headcount-input" />
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-timeline">Timeline</label>
                    <select id="hr-timeline" className={selectCls} value={form.timeline} onChange={set("timeline")} data-testid="hire-timeline-select">
                      <option value="">Select a timeline</option>
                      {TIMELINES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="nx-label" htmlFor="hr-salary">Salary Range</label>
                    <input id="hr-salary" className="nx-input" placeholder="e.g. AED 45,000 – 60,000 / month" value={form.salary_range} onChange={set("salary_range")} data-testid="hire-salary-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="nx-label" htmlFor="hr-details">Role Details</label>
                    <textarea id="hr-details" rows={5} className="nx-input" placeholder="Mandate context, must-haves, team structure…" value={form.details} onChange={set("details")} data-testid="hire-details-input" />
                  </div>
                </div>
                <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                <label className="mt-7 flex items-start gap-3 text-xs leading-relaxed text-white/50" data-testid="hire-consent-label">
                  <input type="checkbox" required checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" data-testid="hire-consent-checkbox" />
                  <span>I consent to NEXREQ processing this information to respond to my enquiry, as described in the <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>.</span>
                </label>
                <button type="submit" className="nx-btn-primary mt-7 w-full sm:w-auto" disabled={submitting} data-testid="hire-submit-btn">
                  {submitting ? "Submitting…" : "Submit Hiring Requirement"} <ArrowRight size={15} />
                </button>
                <p className="mt-5 text-xs leading-relaxed text-white/35">
                  Reviewed by a senior consultant. Response within one business day.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

export default HireTalent;
